import { Footer, Navbar } from '@/components/layout';
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
    getListCoursesQueryKey,
    useCreateAppointment,
    useListCourses,
} from '@/hooks/use-api';
import { useToast } from '@/hooks/use-toast';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
    HiOutlineAcademicCap,
    HiOutlineArrowLongRight,
    HiOutlineBriefcase,
    HiOutlineBuildingLibrary,
    HiOutlineCheckCircle,
    HiOutlineClipboardDocumentCheck,
    HiOutlineHandRaised,
    HiOutlineLockClosed,
    HiOutlineShieldCheck,
    HiOutlineStar,
    HiOutlineWindow,
    HiOutlineWrenchScrewdriver,
} from 'react-icons/hi2';
import { Link, useLocation } from 'wouter';
import * as z from 'zod';
import heroBg from '/hero-bg.png';

const HERO_IMAGES = [
  heroBg,
  'https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=1920&q=80',
  'https://images.unsplash.com/photo-1555952517-2e8e729e0b44?auto=format&fit=crop&w=1920&q=80',
];

const fadeIn = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as any },
  },
};

const services = [
  {
    icon: HiOutlineWindow,
    title: 'Glass Restoration',
    desc: 'Master the art of hard water stain removal, scratch repair, and flawless finishing on commercial glass at any height.',
  },
  {
    icon: HiOutlineWrenchScrewdriver,
    title: 'Rope Access & Rigging',
    desc: 'Rigging, descent control, and emergency protocols — taught by working professionals with 15+ years in the trade.',
  },
  {
    icon: HiOutlineShieldCheck,
    title: 'OSHA & Safety',
    desc: 'Full certification and safety training meeting and exceeding strict federal regulations. Your life depends on this.',
  },
  {
    icon: HiOutlineBuildingLibrary,
    title: 'Swing Stage Operations',
    desc: 'Powered platform rigging, operation, and troubleshooting for the full spectrum of commercial deployments.',
  },
  {
    icon: HiOutlineBriefcase,
    title: 'Business Development',
    desc: 'Win commercial contracts, build your fleet, and scale your own high-rise operation with real business tools.',
  },
  {
    icon: HiOutlineAcademicCap,
    title: 'Industry Certification',
    desc: 'Graduate with nationally recognized credentials. PASMA, IRATA Levels 1–3, and First Aid at Height included.',
  },
];

const enrollmentSchema = z.object({
  courseId: z.string().min(1, 'Please select a course'),
  userName: z.string().min(2, 'Name required'),
  userEmail: z.string().email('Invalid email'),
  userPhone: z.string().optional(),
});

export default function Home() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [bgIndex, setBgIndex] = useState(0);
  const [wipeTriggered, setWipeTriggered] = useState(false);
  const { data: courses, isLoading: isLoadingCourses } = useListCourses({
    query: { queryKey: getListCoursesQueryKey() },
  });
  const createAppointment = useCreateAppointment();

  useEffect(() => {
    setTimeout(() => setWipeTriggered(true), 300);
    const interval = setInterval(() => {
      setBgIndex(i => (i + 1) % HERO_IMAGES.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const form = useForm<z.infer<typeof enrollmentSchema>>({
    resolver: zodResolver(enrollmentSchema),
    defaultValues: { courseId: '', userName: '', userEmail: '', userPhone: '' },
  });

  async function onEnroll(values: z.infer<typeof enrollmentSchema>) {
    const selectedCourse = courses?.find(
      c => c.id.toString() === values.courseId
    );
    if (!selectedCourse) return;
    const scheduledAt = new Date();
    scheduledAt.setDate(scheduledAt.getDate() + 14);
    scheduledAt.setHours(9, 0, 0, 0);
    try {
      await createAppointment.mutateAsync({
        data: {
          courseId: selectedCourse.id,
          courseTitle: selectedCourse.title,
          price: selectedCourse.price,
          userName: values.userName,
          userEmail: values.userEmail,
          userPhone: values.userPhone || null,
          scheduledAt: scheduledAt.toISOString(),
        },
      });
      toast({
        title: 'Enrollment Received',
        description: "We'll contact you within 24 hours to confirm.",
      });
      setLocation('/dashboard');
    } catch {
      toast({
        title: 'Enrollment Failed',
        description: 'Please try again or use the full booking page.',
        variant: 'destructive',
      });
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white text-[#0d1e30] overflow-x-hidden">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative h-screen min-h-[600px] sm:min-h-[640px] flex items-center justify-center overflow-hidden">
        {/* Background crossfade */}
        {HERO_IMAGES.map((src, i) => (
          <div
            key={src}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-[2000ms]"
            style={{
              backgroundImage: `url(${src})`,
              opacity: i === bgIndex ? 1 : 0,
              zIndex: 0,
            }}
          />
        ))}

        {/* Wipe overlay — water streaks on right side that clear away on load */}
        <div
          className="absolute inset-0 z-10 pointer-events-none"
          style={{
            background:
              'linear-gradient(135deg, rgba(255,255,255,0) 0%, rgba(200,230,255,0.08) 40%, rgba(255,255,255,0.04) 100%)',
            backdropFilter: 'blur(0.5px)',
          }}
        />

        {/* Animated squeegee wipe — clean half reveals on load */}
        <div
          className="absolute inset-0 z-11 pointer-events-none transition-all duration-[2200ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            background:
              'linear-gradient(90deg, transparent 0%, rgba(180,215,240,0.22) 55%, rgba(190,220,245,0.38) 72%, rgba(210,235,255,0.55) 85%, rgba(230,245,255,0.72) 100%)',
            clipPath: wipeTriggered ? 'inset(0 0% 0 100%)' : 'inset(0 0% 0 0%)',
          }}
        />

        {/* Dark gradient overlay */}
        <div className="absolute inset-0 z-20 bg-gradient-to-br from-[#0a2540]/50 via-[#0a2540]/35 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-40 z-20 bg-gradient-to-t from-[#0a2540]/60 to-transparent" />

        {/* Hero content */}
        <div className="relative z-30 max-w-7xl mx-auto px-6 lg:px-8 text-center text-white">
          <motion.div initial="hidden" animate="visible" variants={fadeIn}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="inline-flex items-center gap-2 mb-8"
            >
              <span className="w-5 h-px bg-[#0ea5e9]" />
              <span className="font-rajdhani font-700 tracking-[0.28em] text-[#0ea5e9] text-xs uppercase">
                Now Enrolling — 2025 Classes Open
              </span>
              <span className="w-5 h-px bg-[#0ea5e9]" />
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                delay: 0.2,
                duration: 0.9,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="font-bebas font-light text-[clamp(3rem,8vw,6rem)] leading-[1.1] tracking-[0.08em] mb-12"
              style={{ textShadow: '0 4px 60px rgba(0,0,0,0.35)' }}
            >
              WINDOWS CLEANING <span className="text-[#0ea5e9]">SCHOOL</span>
              <span className="text-[#0ea5e9]/70 text-[0.7em]">.COM</span>
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link href="/book">
                <Button
                  size="lg"
                  className="font-rajdhani font-bold tracking-[0.16em] text-sm h-14 px-10 rounded-full bg-[#0ea5e9] hover:bg-[#0369a1] text-white shadow-[0_8px_32px_rgba(14,165,233,0.45)] transition-all hover:shadow-[0_12px_40px_rgba(14,165,233,0.55)] hover:-translate-y-0.5"
                >
                  BROWSE CLASSES
                </Button>
              </Link>
              <Link href="/#wwd">
                <Button
                  size="lg"
                  variant="outline"
                  className="font-rajdhani font-bold tracking-[0.12em] text-sm h-14 px-10 rounded-full bg-white/10 border-white/25 text-white hover:bg-white/20 backdrop-blur-sm"
                >
                  WHAT WE DO
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Stats bar at bottom of hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="absolute bottom-0 left-0 right-0 z-30 grid grid-cols-2 md:grid-cols-4"
          style={{
            background: 'rgba(255,255,255,0.10)',
            backdropFilter: 'blur(20px)',
            borderTop: '1px solid rgba(255,255,255,0.15)',
          }}
        >
          {[
            { n: '1,200+', l: 'Graduates' },
            { n: '98%', l: 'Employment' },
            { n: '15 Yrs', l: 'Experience' },
            { n: '6 Cities', l: 'Nationwide' },
          ].map((s, i) => (
            <div
              key={i}
              className="py-5 text-center"
              style={{
                borderRight:
                  i < 3 ? '1px solid rgba(255,255,255,0.1)' : undefined,
              }}
            >
              <div
                className="font-bebas text-3xl text-white leading-none mb-1"
                style={{ textShadow: '0 0 20px rgba(14,165,233,0.5)' }}
              >
                {s.n}
              </div>
              <div className="font-rajdhani font-600 text-[0.62rem] tracking-[0.22em] uppercase text-white/55">
                {s.l}
              </div>
            </div>
          ))}
        </motion.div>
      </section>

      {/* ── WHAT WE DO ── */}
      <section id="wwd" className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="w-6 h-px bg-[#0ea5e9]" />
              <span className="font-rajdhani font-bold text-[0.68rem] tracking-[0.36em] uppercase text-[#0ea5e9]">
                What We Do
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
              <h2 className="font-bebas text-[clamp(2.8rem,5.5vw,4.8rem)] leading-[0.95] tracking-[0.02em] text-[#0a2540]">
                Glass Cleaning
                <br />
                At Every Height
              </h2>
              <p className="font-sans font-light text-[#94a3b8] leading-[1.82] text-[0.96rem] max-w-[520px]">
                We provide professional high-rise windows cleaning services and
                train the next generation of certified technicians. Whether you
                need a building cleaned or a career that takes you to the top —
                we're your starting point.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        delay: i * 0.08,
                        duration: 0.6,
                        ease: [0.16, 1, 0.3, 1],
                      },
                    },
                  }}
                  className="group relative bg-white rounded-2xl p-9 transition-all duration-300 hover:-translate-y-1.5"
                  style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.07)' }}
                  onMouseEnter={e =>
                    (e.currentTarget.style.boxShadow =
                      '0 12px 48px rgba(10,37,64,0.12)')
                  }
                  onMouseLeave={e =>
                    (e.currentTarget.style.boxShadow =
                      '0 4px 20px rgba(10,37,64,0.07)')
                  }
                >
                  <div className="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl bg-gradient-to-r from-[#0ea5e9] to-[#38bdf8] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
                  <span className="font-bebas text-[3.2rem] text-[#0ea5e9]/06 absolute top-3 right-4 leading-none select-none">
                    0{i + 1}
                  </span>
                  <div className="w-12 h-12 rounded-xl bg-[#e0f4fd] flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6 text-[#0369a1]" />
                  </div>
                  <h3 className="font-rajdhani font-bold text-[0.92rem] tracking-[0.08em] uppercase text-[#0a2540] mb-3">
                    {service.title}
                  </h3>
                  <p className="font-sans font-light text-[0.84rem] text-[#94a3b8] leading-[1.72]">
                    {service.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CLASSES ── */}
      <section id="classes" className="py-28 bg-[#f7fafd]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="mb-16"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="w-6 h-px bg-[#0ea5e9]" />
              <span className="font-rajdhani font-bold text-[0.68rem] tracking-[0.36em] uppercase text-[#0ea5e9]">
                Classes & Pricing
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
              <h2 className="font-bebas text-[clamp(2.8rem,5.5vw,4.8rem)] leading-[0.95] tracking-[0.02em] text-[#0a2540]">
                Choose Your
                <br />
                Training Path
              </h2>
              <p className="font-sans font-light text-[#94a3b8] leading-[1.82] text-[0.96rem] max-w-[520px]">
                Three structured pathways from complete beginner to master
                technician. All courses include hands-on practical hours,
                equipment provided, and expert instructors.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {isLoadingCourses
              ? Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-[480px] w-full rounded-2xl" />
                ))
              : courses && Array.isArray(courses)
                ? courses.slice(0, 3).map((course, i) => {
                    const isFeat = course.level === 'intermediate';
                    const feats: { [key: string]: string[] } = {
                      beginner: [
                        '5-day intensive course',
                        'Working at height theory',
                        'PPE selection & inspection',
                        'Basic rope skills',
                        'Health & safety certificate',
                        'All equipment provided',
                      ],
                      intermediate: [
                        '12-day comprehensive course',
                        'IRATA Level 1 prep',
                        'Rope access practical hours',
                        'Swing stage operations',
                        'Chemical & glass care',
                        'Job placement support',
                        'All equipment included',
                      ],
                      advanced: [
                        '20-day master programme',
                        'IRATA Level 2 & 3 prep',
                        'Supervisor certification',
                        'Rescue systems & drills',
                        'Business launch module',
                        '1-on-1 mentorship hours',
                        'Lifetime alumni network',
                      ],
                    };
                    const items = feats[course.level] || [];
                    return (
                      <motion.div
                        key={course.id}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={{
                          hidden: { opacity: 0, y: 20 },
                          visible: {
                            opacity: 1,
                            y: 0,
                            transition: {
                              delay: i * 0.1,
                              duration: 0.6,
                              ease: [0.16, 1, 0.3, 1],
                            },
                          },
                        }}
                        className={`relative rounded-2xl overflow-hidden flex flex-col ${isFeat ? 'md:-mt-4 md:mb-4' : ''}`}
                        style={{
                          background: isFeat
                            ? 'linear-gradient(145deg, #0a2540, #0369a1)'
                            : 'white',
                          boxShadow: isFeat
                            ? '0 24px 80px rgba(10,37,64,0.22)'
                            : '0 4px 20px rgba(10,37,64,0.07)',
                        }}
                      >
                        {isFeat && (
                          <div className="absolute top-5 right-5 bg-[#0ea5e9] text-white font-rajdhani font-bold text-[0.58rem] tracking-[0.18em] uppercase px-3 py-1 rounded-full">
                            Most Popular
                          </div>
                        )}
                        <div className="p-9 flex flex-col flex-1">
                          <div
                            className={`font-rajdhani font-600 text-[0.64rem] tracking-[0.3em] uppercase mb-2 ${isFeat ? 'text-[rgba(14,165,233,0.8)]' : 'text-[#0ea5e9]'}`}
                          >
                            Level 0{i + 1} —{' '}
                            {course.level.charAt(0).toUpperCase() +
                              course.level.slice(1)}
                          </div>
                          <h3
                            className={`font-bebas text-[2rem] leading-[1.05] tracking-[0.04em] mb-3 ${isFeat ? 'text-white' : 'text-[#0a2540]'}`}
                          >
                            {course.title}
                          </h3>
                          <p
                            className={`font-sans font-light text-[0.84rem] leading-[1.72] mb-5 ${isFeat ? 'text-white/60' : 'text-[#94a3b8]'}`}
                          >
                            {course.description}
                          </p>
                          <div
                            className={`font-bebas text-[3rem] leading-none mb-1 tracking-[0.02em] ${isFeat ? 'text-white' : 'text-[#0a2540]'}`}
                          >
                            ${course.price}{' '}
                            <span
                              className={`font-sans text-[0.75rem] font-light tracking-normal ${isFeat ? 'text-white/50' : 'text-[#94a3b8]'}`}
                            >
                              / course
                            </span>
                          </div>
                          <div
                            className={`h-px my-4 ${isFeat ? 'bg-white/12' : 'bg-[rgba(0,0,0,0.07)]'}`}
                          />
                          <ul className="flex flex-col gap-2 mb-7 flex-1">
                            {items.map((feat, fi) => (
                              <li
                                key={fi}
                                className={`font-sans font-light text-[0.83rem] flex items-center gap-2.5 ${isFeat ? 'text-white/65' : 'text-[#94a3b8]'}`}
                              >
                                <span
                                  className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 text-[0.62rem] font-bold ${isFeat ? 'bg-white/15 text-white' : 'bg-[#e0f4fd] text-[#0369a1]'}`}
                                >
                                  ✓
                                </span>
                                {feat}
                              </li>
                            ))}
                          </ul>
                          <Link href="/book" className="block w-full">
                            <button
                              className={`w-full text-center py-3.5 rounded-full font-rajdhani font-bold text-[0.78rem] tracking-[0.18em] uppercase transition-all duration-200 cursor-pointer ${
                                isFeat
                                  ? 'bg-white text-[#0a2540] hover:bg-[#0ea5e9] hover:text-white'
                                  : 'bg-[#e0f4fd] text-[#0369a1] hover:bg-[#0ea5e9] hover:text-white hover:shadow-[0_8px_24px_rgba(14,165,233,0.35)]'
                              }`}
                            >
                              Enroll — ${course.price}
                            </button>
                          </Link>
                        </div>
                      </motion.div>
                    );
                  })
                : null}
          </div>
        </div>
      </section>

      {/* ── 2025 SCHEDULE ── */}
      <section className="py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="w-6 h-px bg-[#0ea5e9]" />
              <span className="font-rajdhani font-bold text-[0.68rem] tracking-[0.36em] uppercase text-[#0ea5e9]">
                2025 Schedule
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end">
              <h2 className="font-bebas text-[clamp(2.8rem,5.5vw,4.8rem)] leading-[0.95] tracking-[0.02em] text-[#0a2540]">
                Upcoming
                <br />
                Sessions
              </h2>
              <p className="font-sans font-light text-[#94a3b8] leading-[1.82] text-[0.96rem]">
                Limited spots per cohort ensure quality instruction. Sessions
                fill fast — secure your place as soon as you're ready to commit.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="overflow-hidden rounded-2xl"
            style={{ boxShadow: '0 12px 48px rgba(10,37,64,0.12)' }}
          >
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#0a2540' }}>
                  {[
                    'Date',
                    'Course',
                    'Level',
                    'Days',
                    'Location',
                    'Spots',
                    'Price',
                  ].map(h => (
                    <th
                      key={h}
                      className="font-rajdhani font-bold text-[0.63rem] tracking-[0.26em] uppercase text-white/70 text-left py-4 px-5 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white font-sans">
                {[
                  {
                    date: 'May 5',
                    course: 'Ground Floor Foundation',
                    level: 'Beginner',
                    days: 5,
                    loc: 'Los Angeles, CA',
                    spots: 4,
                    low: false,
                    price: 499,
                  },
                  {
                    date: 'May 12',
                    course: 'Sky Level Professional',
                    level: 'Intermediate',
                    days: 12,
                    loc: 'Chicago, IL',
                    spots: 2,
                    low: true,
                    price: 1199,
                  },
                  {
                    date: 'May 19',
                    course: 'Ground Floor Foundation',
                    level: 'Beginner',
                    days: 5,
                    loc: 'New York, NY',
                    spots: 8,
                    low: false,
                    price: 499,
                  },
                  {
                    date: 'Jun 2',
                    course: 'Apex Master Class',
                    level: 'Advanced',
                    days: 20,
                    loc: 'Houston, TX',
                    spots: 2,
                    low: true,
                    price: 2499,
                  },
                  {
                    date: 'Jun 9',
                    course: 'Sky Level Professional',
                    level: 'Intermediate',
                    days: 12,
                    loc: 'Miami, FL',
                    spots: 6,
                    low: false,
                    price: 1199,
                  },
                  {
                    date: 'Jun 23',
                    course: 'Ground Floor Foundation',
                    level: 'Beginner',
                    days: 5,
                    loc: 'Seattle, WA',
                    spots: 10,
                    low: false,
                    price: 499,
                  },
                ].map((row, i) => {
                  const lvlColor =
                    row.level === 'Beginner'
                      ? 'bg-[rgba(14,165,233,0.1)] text-[#0369a1]'
                      : row.level === 'Intermediate'
                        ? 'bg-[rgba(245,158,11,0.1)] text-amber-700'
                        : 'bg-[rgba(239,68,68,0.1)] text-red-700';
                  return (
                    <tr
                      key={i}
                      className="border-b border-[rgba(0,0,0,0.05)] last:border-0 hover:bg-[#f7fafd] transition-colors"
                    >
                      <td className="py-5 px-5 font-sans text-[0.86rem] text-[#0d1e30]">
                        {row.date}
                      </td>
                      <td className="py-5 px-5 font-rajdhani font-bold text-[0.9rem] text-[#0a2540]">
                        {row.course}
                      </td>
                      <td className="py-5 px-5">
                        <span
                          className={`inline-block px-3 py-1 rounded-full font-rajdhani font-bold text-[0.60rem] tracking-[0.12em] uppercase ${lvlColor}`}
                        >
                          {row.level}
                        </span>
                      </td>
                      <td className="py-5 px-5 text-[0.86rem] text-[#94a3b8]">
                        {row.days}
                      </td>
                      <td className="py-5 px-5 text-[0.86rem] text-[#94a3b8]">
                        {row.loc}
                      </td>
                      <td className="py-5 px-5">
                        <span
                          className={`font-rajdhani font-bold text-[0.86rem] ${row.low ? 'text-[#ef4444]' : 'text-[#0369a1]'}`}
                        >
                          {row.spots} left
                        </span>
                      </td>
                      <td className="py-5 px-5 font-sans text-[0.86rem] text-[#0d1e30]">
                        ${row.price}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── PREREQUISITES ── */}
      <section className="py-28 bg-[#0a2540] relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-5 pointer-events-none"
          style={{
            background:
              'repeating-linear-gradient(90deg, #fff 0, #fff 1px, transparent 1px, transparent 80px), repeating-linear-gradient(0deg, #fff 0, #fff 1px, transparent 1px, transparent 80px)',
          }}
        />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="mb-14"
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="w-6 h-px bg-[#0ea5e9]" />
              <span className="font-rajdhani font-bold text-[0.68rem] tracking-[0.36em] uppercase text-[#0ea5e9]">
                Before You Enroll
              </span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end">
              <h2 className="font-bebas text-[clamp(2.8rem,5.5vw,4.8rem)] leading-[0.95] tracking-[0.02em] text-white">
                What You'll
                <br />
                Need To Join
              </h2>
              <p className="font-sans font-light text-[rgba(255,255,255,0.55)] leading-[1.82] text-[0.96rem]">
                Our courses are open to motivated adults with no prior
                experience required. However, we do ask all students to meet the
                following baseline requirements before their first day.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                n: '01',
                label: 'Government-Issued ID',
                detail:
                  'Valid photo ID required for insurance registration and OSHA documentation.',
              },
              {
                n: '02',
                label: 'Steel-Toe Safety Boots',
                detail:
                  "ANSI-rated boots covering the ankle. We'll inspect them on day one — no exceptions.",
              },
              {
                n: '03',
                label: 'Background Check',
                detail:
                  "A clean criminal background check is required. We'll guide you through the process.",
              },
              {
                n: '04',
                label: 'Baseline Fitness',
                detail:
                  'Ability to walk a flight of stairs and stand for extended periods. No extreme fitness required.',
              },
              {
                n: '05',
                label: 'Online Safety Module',
                detail:
                  'A free 2-hour pre-course safety module completed before your first session. Sent on enrollment.',
              },
              {
                n: '06',
                label: 'Enrollment Deposit',
                detail:
                  '$200 non-refundable deposit secures your spot. Counted towards your total course fee.',
              },
            ].map((req, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  hidden: { opacity: 0, y: 16 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: i * 0.06,
                      duration: 0.5,
                      ease: [0.16, 1, 0.3, 1],
                    },
                  },
                }}
                className="rounded-2xl p-6"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <span className="font-bebas text-[3rem] leading-none text-[rgba(14,165,233,0.2)] block mb-2">
                  {req.n}
                </span>
                <div className="font-rajdhani font-bold text-[0.84rem] tracking-[0.08em] uppercase text-white mb-2">
                  {req.label}
                </div>
                <p className="font-sans font-light text-[0.82rem] text-[rgba(255,255,255,0.5)] leading-[1.72]">
                  {req.detail}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Step-by-step enrollment process */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="mt-16"
          >
            <div className="font-rajdhani font-bold text-[0.68rem] tracking-[0.36em] uppercase text-[#0ea5e9] mb-6">
              How Enrollment Works
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                {
                  step: '1',
                  title: 'Submit Form',
                  desc: 'Fill out the enrollment form with your course choice and contact details.',
                },
                {
                  step: '2',
                  title: 'We Call You',
                  desc: 'Our team calls within 24 hours to confirm your eligibility and answer questions.',
                },
                {
                  step: '3',
                  title: 'Pay Deposit',
                  desc: 'Secure your spot with the $200 deposit. Balance due on course start date.',
                },
                {
                  step: '4',
                  title: 'Show Up Ready',
                  desc: 'Complete the online module, bring your gear, and start your career.',
                },
              ].map((s, i) => (
                <div key={i} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 rounded-full bg-[#0ea5e9] flex items-center justify-center font-bebas text-lg text-white shrink-0">
                      {s.step}
                    </div>
                    {i < 3 && (
                      <div
                        className="w-px flex-1 mt-2"
                        style={{ background: 'rgba(14,165,233,0.3)' }}
                      />
                    )}
                  </div>
                  <div className="pt-1 pb-6">
                    <div className="font-rajdhani font-bold text-[0.84rem] tracking-[0.08em] uppercase text-white mb-1">
                      {s.title}
                    </div>
                    <p className="font-sans font-light text-[0.8rem] text-[rgba(255,255,255,0.5)] leading-[1.7]">
                      {s.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── WE COME TO YOU ── */}
      <section className="py-28 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-6 h-px bg-[#0ea5e9]" />
                <span className="font-rajdhani font-bold text-[0.68rem] tracking-[0.36em] uppercase text-[#0ea5e9]">
                  On-Site Training
                </span>
              </div>
              <h2 className="font-bebas text-[clamp(2.8rem,5.5vw,4.8rem)] leading-[0.95] tracking-[0.02em] text-[#0a2540] mb-5">
                Can't Come To Us?
                <br />
                <span className="text-[#0ea5e9]">We Come To You.</span>
              </h2>
              <p className="font-sans font-light text-[#94a3b8] leading-[1.82] text-[0.96rem] mb-8">
                Already running a windows cleaning company? We offer fully mobile
                on-site training programmes tailored for existing teams. From
                2-person crews to full commercial fleets — we bring the
                curriculum, equipment, and certifications directly to your
                location.
              </p>
              <div className="flex flex-col gap-4 mb-8">
                {[
                  {
                    title: 'Minimum 2 participants',
                    desc: 'On-site training is available for groups of 2 or more.',
                  },
                  {
                    title: 'Any state, any city',
                    desc: 'We travel anywhere in the continental United States.',
                  },
                  {
                    title: 'Custom curriculum',
                    desc: "We design the training around your team's current skill level and equipment.",
                  },
                  {
                    title: 'Company certifications',
                    desc: 'Your team receives the same nationally-recognised credentials as our in-person students.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="w-6 h-6 rounded-full bg-[#e0f4fd] flex items-center justify-center shrink-0 mt-0.5">
                      <HiOutlineCheckCircle className="w-3.5 h-3.5 text-[#0369a1]" />
                    </div>
                    <div>
                      <span className="font-rajdhani font-bold text-[0.84rem] tracking-[0.06em] uppercase text-[#0a2540]">
                        {item.title}
                      </span>
                      <span className="font-sans font-light text-[0.84rem] text-[#94a3b8] ml-2">
                        {item.desc}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <Link href="/book">
                <button
                  className="flex items-center gap-2 px-7 py-3.5 rounded-full font-rajdhani font-bold text-[0.76rem] tracking-[0.18em] uppercase text-white cursor-pointer transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #0ea5e9, #0369a1)',
                    boxShadow: '0 8px 28px rgba(14,165,233,0.4)',
                  }}
                >
                  Request On-Site Quote{' '}
                  <HiOutlineArrowLongRight className="w-5 h-5" />
                </button>
              </Link>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, x: 30 },
                visible: {
                  opacity: 1,
                  x: 0,
                  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
                },
              }}
            >
              <div
                className="rounded-2xl overflow-hidden"
                style={{ boxShadow: '0 24px 80px rgba(10,37,64,0.18)' }}
              >
                <div className="bg-[#0a2540] px-7 py-6">
                  <div className="font-bebas text-[1.8rem] tracking-[0.04em] text-white mb-1">
                    On-Site Pricing
                  </div>
                  <p className="font-sans font-light text-[0.82rem] text-[rgba(255,255,255,0.55)]">
                    Flexible pricing based on team size and location.
                  </p>
                </div>
                <div className="bg-white px-7 py-6 space-y-4">
                  {[
                    {
                      label: '2–5 People',
                      price: '$1,800',
                      note: 'per person',
                      detail: '5-day intensive, all levels',
                    },
                    {
                      label: '6–12 People',
                      price: '$1,400',
                      note: 'per person',
                      detail: 'Volume discount, 5 days',
                    },
                    {
                      label: '13+ People',
                      price: 'Custom',
                      note: 'quote',
                      detail: 'Enterprise, multi-day modules',
                    },
                    {
                      label: 'Travel & Setup',
                      price: '$500',
                      note: 'flat fee',
                      detail: 'Continental US only',
                    },
                  ].map((tier, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between py-3"
                      style={{
                        borderBottom:
                          i < 3 ? '1px solid rgba(0,0,0,0.06)' : undefined,
                      }}
                    >
                      <div>
                        <div className="font-rajdhani font-bold text-[0.86rem] tracking-[0.06em] text-[#0a2540]">
                          {tier.label}
                        </div>
                        <div className="font-sans text-[0.76rem] text-[#94a3b8]">
                          {tier.detail}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bebas text-[1.6rem] leading-none text-[#0a2540]">
                          {tier.price}
                        </div>
                        <div className="font-sans text-[0.7rem] text-[#94a3b8]">
                          {tier.note}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── ENROLL ── */}
      <section
        id="signup"
        className="py-28 bg-[#eef3f9] relative overflow-hidden"
      >
        <div
          className="absolute top-[-300px] right-[-200px] w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 65%)',
          }}
        />
        <div
          className="absolute bottom-[-200px] left-[-200px] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(14,165,233,0.08) 0%, transparent 65%)',
          }}
        />
        <div className="max-w-6xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="lg:sticky lg:top-24"
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="w-6 h-px bg-[#0ea5e9]" />
                <span className="font-rajdhani font-bold text-[0.68rem] tracking-[0.36em] uppercase text-[#0ea5e9]">
                  Student Enrolment
                </span>
              </div>
              <h2 className="font-bebas text-[clamp(2.8rem,5.5vw,4.8rem)] leading-[0.95] tracking-[0.02em] text-[#0a2540] mb-4">
                Start Your
                <br />
                Ascent Today
              </h2>
              <p className="font-sans font-light text-[#94a3b8] text-[0.96rem] leading-[1.82] mb-10">
                Submit the form and our team will contact you within 24 hours to
                confirm your place and send your full course pack.
              </p>
              <div className="flex flex-col gap-6">
                {[
                  {
                    icon: HiOutlineLockClosed,
                    title: 'Secure Booking',
                    desc: 'Full refund available up to 14 days before your course start date.',
                  },
                  {
                    icon: HiOutlineClipboardDocumentCheck,
                    title: 'Instant Confirmation',
                    desc: 'Receive your course pack and equipment checklist by email right away.',
                  },
                  {
                    icon: HiOutlineHandRaised,
                    title: 'Job Placement Help',
                    desc: 'Access our industry partner network and active job board on graduation.',
                  },
                  {
                    icon: HiOutlineStar,
                    title: 'Nationally Recognised',
                    desc: 'All certificates fully accredited and valid across the US and internationally.',
                  },
                ].map((p, i) => (
                  <motion.div
                    key={i}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    variants={{
                      hidden: { opacity: 0, x: -16 },
                      visible: {
                        opacity: 1,
                        x: 0,
                        transition: { delay: i * 0.1, duration: 0.5 },
                      },
                    }}
                    className="flex gap-4 items-start"
                  >
                    <div
                      className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shrink-0"
                      style={{ boxShadow: '0 4px 20px rgba(10,37,64,0.07)' }}
                    >
                      <p.icon className="w-5 h-5 text-[#0369a1]" />
                    </div>
                    <div>
                      <div className="font-rajdhani font-bold text-[0.88rem] tracking-[0.08em] uppercase text-[#0a2540] mb-1">
                        {p.title}
                      </div>
                      <p className="font-sans font-light text-[0.82rem] text-[#94a3b8] leading-[1.6]">
                        {p.desc}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="bg-white rounded-2xl p-10"
              style={{ boxShadow: '0 24px 80px rgba(10,37,64,0.18)' }}
            >
              <div className="font-bebas text-[2.2rem] tracking-[0.04em] text-[#0a2540] mb-1">
                Enrolment Form
              </div>
              <p className="font-sans font-light text-[0.84rem] text-[#94a3b8] mb-8">
                All fields required — we respond within 24 hours.
              </p>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(onEnroll)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="courseId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-rajdhani font-bold tracking-[0.24em] text-[0.64rem] uppercase text-[#94a3b8]">
                          Select Course
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="h-12 bg-[#f7fafd] border-transparent focus:border-[#0ea5e9] focus:ring-[#0ea5e9]/10 rounded-xl">
                              <SelectValue placeholder="Choose your level…" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {courses?.map(c => (
                              <SelectItem key={c.id} value={c.id.toString()}>
                                {c.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="userName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="font-rajdhani font-bold tracking-[0.24em] text-[0.64rem] uppercase text-[#94a3b8]">
                          Full Name
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="John Smith"
                            className="h-12 bg-[#f7fafd] border-transparent focus:border-[#0ea5e9] rounded-xl"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="userEmail"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-rajdhani font-bold tracking-[0.24em] text-[0.64rem] uppercase text-[#94a3b8]">
                            Email
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="john@example.com"
                              type="email"
                              className="h-12 bg-[#f7fafd] border-transparent focus:border-[#0ea5e9] rounded-xl"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="userPhone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="font-rajdhani font-bold tracking-[0.24em] text-[0.64rem] uppercase text-[#94a3b8]">
                            Phone
                          </FormLabel>
                          <FormControl>
                            <Input
                              placeholder="+1 (555) 000-0000"
                              className="h-12 bg-[#f7fafd] border-transparent focus:border-[#0ea5e9] rounded-xl"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={createAppointment.isPending}
                    className="w-full h-14 mt-2 rounded-full font-rajdhani font-bold tracking-[0.18em] text-[0.82rem] uppercase bg-[#0ea5e9] text-white transition-all hover:bg-[#0369a1] hover:-translate-y-0.5 cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                    style={{ boxShadow: '0 8px 28px rgba(14,165,233,0.4)' }}
                  >
                    {createAppointment.isPending ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        Submit Enrolment Application{' '}
                        <HiOutlineArrowLongRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </Form>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
